import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, MapPin, ShieldCheck, ShieldOff, AlertCircle, Trash2 } from "lucide-react";
import { OPPORTUNITY_TYPES, TYPE_LABEL } from "@/lib/permissions";

const STATUS_META = {
  available: { label: "Available", color: "bg-emerald-500 text-white", border: "border-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  under_review: { label: "Under review", color: "bg-amber-500 text-white", border: "border-amber-500", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  assigned: { label: "Assigned", color: "bg-red-500 text-white", border: "border-red-500", chip: "bg-red-50 text-red-700 border-red-200" },
  reserved: { label: "Reserved", color: "bg-zinc-700 text-white", border: "border-zinc-700", chip: "bg-zinc-100 text-zinc-700 border-zinc-300" },
};

const emptyForm = {
  country: "",
  state: "",
  district: "",
  city: "",
  opportunity_type: "",
  notes: "",
};

export default function Territories() {
  const { user } = useAuth();
  const [scope, setScope] = useState("all"); // all / mine / assigned
  const [status, setStatus] = useState("");
  const [country, setCountry] = useState("");
  const [type, setType] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const params = { scope };
    if (status) params.status = status;
    if (country) params.country = country;
    if (type) params.opportunity_type = type;
    const { data } = await api.get("/territories", { params });
    setItems(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [scope, status, country, type]);

  const apply = async (e) => {
    e.preventDefault();
    try {
      await api.post("/territories/apply", form);
      toast.success("Territory application submitted");
      setOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const withdraw = async (t) => {
    if (!window.confirm(`Withdraw application for ${t.city || t.state || t.country}?`)) return;
    try {
      await api.delete(`/territories/${t.territory_id}`);
      toast.success("Withdrawn");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const stats = useMemo(() => {
    const acc = { available: 0, under_review: 0, assigned: 0, reserved: 0 };
    items.forEach((t) => { acc[t.status] = (acc[t.status] || 0) + 1; });
    return acc;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="territories-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tag"><span className="w-1.5 h-1.5 bg-[#94959A]" /> Territory</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">Territory board</h1>
          <p className="text-zinc-600 text-sm mt-1">
            Apply for coverage in a country, state, district or city. Admins review and assign.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="territory-apply-btn"
          className="inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white px-4 py-2.5 font-semibold transition-colors text-sm"
        >
          <Plus className="w-4 h-4" /> Apply for territory
        </button>
      </div>

      {/* Legend */}
      <div className="border border-zinc-200 bg-white p-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="territory-legend">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <span className={`w-3 h-3 ${m.color}`} />
            <span className="font-semibold uppercase tracking-widest">{m.label}</span>
            <span className="ml-auto font-mono text-zinc-500">{stats[k] || 0}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["all", "mine", "assigned"].map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            data-testid={`territory-scope-${s}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
              scope === s ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"
            }`}
          >{s}</button>
        ))}
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="territory-filter-status"
          className="text-xs uppercase tracking-widest font-semibold border border-zinc-300 px-2 py-1.5 bg-white"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          data-testid="territory-filter-type"
          className="text-xs uppercase tracking-widest font-semibold border border-zinc-300 px-2 py-1.5 bg-white"
        >
          <option value="">All types</option>
          {OPPORTUNITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.short}</option>)}
        </select>
        <input
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          data-testid="territory-filter-country"
          placeholder="Country (exact match)"
          className="text-xs border border-zinc-300 px-2 py-1.5 outline-none focus:border-[#C8102E]"
        />
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="territories-table">
          <thead>
            <tr className="text-left text-zinc-500 overline border-b border-zinc-200">
              <th className="p-3">Location</th>
              <th className="p-3">For type</th>
              <th className="p-3">Applicant</th>
              <th className="p-3">Assigned to</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No territories.</td></tr>
            )}
            {items.map((t) => {
              const s = STATUS_META[t.status] || STATUS_META.under_review;
              const isMine = t.applicant_id === user?.user_id;
              return (
                <tr key={t.territory_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`territory-row-${t.territory_id}`}>
                  <td className="p-3 font-medium">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-6 ${s.color}`} />
                      <div>
                        <div>{t.city || t.district || t.state || t.country}</div>
                        <div className="text-xs text-zinc-500">
                          {[t.country, t.state, t.district].filter(Boolean).join(" › ")}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-zinc-700 text-xs uppercase tracking-widest">{t.opportunity_type ? (TYPE_LABEL[t.opportunity_type] || t.opportunity_type) : "—"}</td>
                  <td className="p-3 text-zinc-700">{t.applicant_name || "—"}</td>
                  <td className="p-3 text-zinc-700">{t.assigned_name || "—"}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold border px-2 py-0.5 ${s.chip}`}>{s.label}</span>
                  </td>
                  <td className="p-3 text-right">
                    {isMine && t.status !== "assigned" && (
                      <button onClick={() => withdraw(t)} data-testid={`territory-withdraw-${t.territory_id}`} className="px-2 py-1 hover:bg-red-50 text-red-600 text-xs font-semibold inline-flex items-center gap-1">
                        <Trash2 className="w-3.5 h-3.5" /> Withdraw
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="territory-dialog">
          <form onSubmit={apply} className="bg-white border border-zinc-300 w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <h2 className="font-display font-black text-xl tracking-tight">Apply for territory</h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1" data-testid="territory-dialog-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-xs text-zinc-500 border border-zinc-200 p-3 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-[#C8102E] shrink-0" />
                Your application is reviewed by an admin. Status will start as Under review (yellow).
              </div>
              <Field label="Country" required value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="territory-field-country" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="State / Region" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="territory-field-state" />
                <Field label="District" value={form.district} onChange={(v) => setForm({ ...form, district: v })} testid="territory-field-district" />
              </div>
              <Field label="City / Area" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="territory-field-city" />
              <div>
                <label className="overline text-zinc-500">For opportunity type (optional)</label>
                <select
                  value={form.opportunity_type}
                  onChange={(e) => setForm({ ...form, opportunity_type: e.target.value })}
                  data-testid="territory-field-type"
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E] bg-white"
                >
                  <option value="">— Any —</option>
                  {OPPORTUNITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="overline text-zinc-500">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="territory-field-notes"
                  rows={3}
                  className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
                />
              </div>
            </div>
            <div className="p-5 border-t border-zinc-200 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 hover:bg-zinc-100 text-sm">Cancel</button>
              <button type="submit" data-testid="territory-submit" className="px-4 py-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white font-semibold text-sm">
                Submit application
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline text-zinc-500">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full border border-zinc-300 px-3 py-2 outline-none focus:border-[#C8102E]"
      />
    </div>
  );
}
